import { IJobParam, IJobParamChangeEvent } from './PA.Job.IF.JobParam'
import { WB_JobParamRow, WB_JobParamSRRow, WB_ScheduleRow, WB_UIAppExecutableRow } from '../PA.Core/PAdbSchemaRows'
import { IMessage, IMessageProvider, MessageType } from '../PA.Core/PA.Core.IF.Basic'
import { PAdbContext } from '../PA.Core/PAdbContext'
import * as _ from 'lodash'
import { MessageProvider } from '../PA.Core/Basic/Messages'
import { ESRecordResolver } from '../PA.Core/lib/ESRecordResolver'
import { esWB_Job_Param, esWB_Job_ParamSR, esWB_Schedule, esWB_UI_AppExecutable } from '../PA.Core/PAdbSchema'
import { BehaviorSubject } from 'rxjs'
import { RRuleUtils } from '../PA.Core/Basic/Dates'

export class JobParam implements IJobParam {
    private _dbContext: PAdbContext
    private _jobParamRow: WB_JobParamRow
    private _loadMessageProvider: MessageProvider
    private _validateMessageProvider: MessageProvider
    private _dctJobSchedule: { [wbId: number]: WB_ScheduleRow }
    private _onPropertyChanged: BehaviorSubject<IJobParamChangeEvent>
    private _bSaved: boolean
    private _bDeleted: boolean
    private _bIsValidated: boolean
    private _bSkipRecordResolution

    constructor(jobParamRow: WB_JobParamRow, options?: { skipRecordResolution: boolean }) {
        this._jobParamRow = jobParamRow
        this._bSkipRecordResolution = !!options?.skipRecordResolution
        this._dbContext = <PAdbContext>jobParamRow.dataStore()
        this._loadMessageProvider = new MessageProvider()
        this._dctJobSchedule = {}
        this._onPropertyChanged = new BehaviorSubject<IJobParamChangeEvent>(undefined)
        this._validateMessageProvider = new MessageProvider()
        this._bSaved = false
        this._bDeleted = false
        this._bIsValidated = false
    }

    get wbMasterDataSetId(): number {
        return this._jobParamRow.cif.WB_MDSET_ID
    }

    get wbCompanyId(): number {
        return this._jobParamRow.cif.WB_COMPANY_ID
    }

    get wbSalesOrgId(): number {
        return this._jobParamRow.cif.WB_SALESORG_ID
    }

    get wb_id(): number {
        return this._jobParamRow.cif.WB_JOB_PARAM_ID
    }

    get id_ui(): string {
        return this._jobParamRow.cif.JOB_PARAM_ID
    }

    set id_ui(val: string) {
        if (this._jobParamRow) this._jobParamRow.cif.JOB_PARAM_ID = val
    }

    get name(): string {
        return this._jobParamRow.cif.JOB_PARAM_NAME
    }

    set name(val: string) {
        if (this._jobParamRow) this._jobParamRow.cif.JOB_PARAM_NAME = val
    }

    get LoadMessageProvider(): IMessageProvider {
        return this._loadMessageProvider
    }

    get dbContext(): PAdbContext {
        return this._dbContext
    }

    get bIsValidated(): boolean {
        return this._bIsValidated
    }

    async OnLoadComplete(): Promise<void> {
        const dbContext = this._dbContext
        const jpr = this._jobParamRow

        if (!this._bSkipRecordResolution) {
            const rr = new ESRecordResolver(dbContext, {
                standardConditions: {
                    [esWB_Job_Param]: { DELETED_IND: { neq: '1' } },
                    [esWB_Job_ParamSR]: { DELETED_IND: { neq: '1' } },
                    [esWB_Schedule]: { DELETED_IND: { neq: '1' } },
                    [esWB_UI_AppExecutable]: { DELETED_IND: { neq: '1' } }
                }
            })
            await rr.findAllWithRelationships(esWB_Job_Param, [jpr.cif.WB_JOB_PARAM_ID])
        }

        _.forEach(this._jobParamRow.WB_JobParamSRRows, (a) => {
            if (!a.isActive) return
            if (!_.isEmpty(a.WB_ScheduleRow)) return
            this._dctJobSchedule[a.cif.WB_SCHEDULE_ID] = a.WB_ScheduleRow
        })
    }

    get WB_JobParamRow(): WB_JobParamRow {
        return this._jobParamRow
    }

    get JobSchedules(): WB_ScheduleRow[] {
        return _.values(this._dctJobSchedule)
    }

    get OnPropertyChanged(): BehaviorSubject<IJobParamChangeEvent> {
        return this._onPropertyChanged
    }

    get ValidateMessageProvider(): MessageProvider {
        return this._validateMessageProvider
    }

    async Validate(): Promise<void> {
        let self = this
        let mp = self._validateMessageProvider
        mp.clearMessages()

        _.forEach(self._jobParamRow.WB_JobParamSRRows, (aRow) => {
            if (_.isEmpty(aRow.WB_ScheduleRow) || !aRow.WB_ScheduleRow.isActive) {
                return
            }

            const bHasTrigger = !_.isEmpty(aRow.WB_JobParamRow.cif.DEPENDENT_TRIGGER_ID)
            const msgs: IMessage[] = RRuleUtils.getValidationMessages(aRow.WB_ScheduleRow, bHasTrigger)
            if (_.filter(msgs, (a) => a.type == MessageType.Error).length > 0) {
                mp.addMessage({
                    type: MessageType.Error,
                    code: 'InvalidSchedule',
                    title: 'Invalid schedule',
                    description: `Can't find any date that meets schedule settings for (${aRow.WB_ScheduleRow.cif.SCHEDULE_NAME})`
                })
            }
        })

        if (mp.countMessages(MessageType.Error) > 0) {
            self._bIsValidated = false
        } else {
            self._bIsValidated = true
        }
    }

    async Save(bBatch?: boolean, options?: { bSkipValidations?: boolean }): Promise<void> {
        const dbContext = this._dbContext

        if (_.isEmpty(this._jobParamRow)) return

        this._bSaved = false
        if (!options?.bSkipValidations) {
            await this.Validate()
            if (!this.bIsValidated) return
        }

        let tasks: Promise<any>[] = []
        if (bBatch) {
            tasks.push(dbContext.SaveBatch(esWB_Job_Param, this._jobParamRow))
            tasks.push(dbContext.SaveBatch(esWB_Job_ParamSR, this._jobParamRow.WB_JobParamSRRows))
            tasks.push(
                dbContext.SaveBatch(
                    esWB_Schedule,
                    _.map(this._jobParamRow.WB_JobParamSRRows, (aRow) => {
                        return aRow.WB_ScheduleRow
                    })
                )
            )
        } else {
            tasks.push(dbContext.Save(esWB_Job_Param, this._jobParamRow))
            tasks.push(dbContext.Save(esWB_Job_ParamSR, this._jobParamRow.WB_JobParamSRRows))
            tasks.push(
                dbContext.Save(
                    esWB_Schedule,
                    _.map(this._jobParamRow.WB_JobParamSRRows, (aRow) => {
                        return aRow.WB_ScheduleRow
                    })
                )
            )
        }

        await Promise.all(tasks)

        this._bSaved = true
    }

    get bSaved(): boolean {
        return this._bSaved
    }

    async addJobSchedule(scheduleRow: WB_ScheduleRow): Promise<void> {
        const dbContext = this._dbContext
        const jobParamRow = this._jobParamRow

        if (_.isEmpty(jobParamRow)) return

        if (_.isEmpty(scheduleRow)) return

        const wbScheduleId = scheduleRow?.cif?.WB_SCHEDULE_ID
        if (_.isUndefined(wbScheduleId)) return
        if (!(wbScheduleId in this._dctJobSchedule)) this._dctJobSchedule[wbScheduleId] = scheduleRow

        // Check if there is a duplicate
        let found = false
        _.forEach(jobParamRow.WB_JobParamSRRows, (a) => {
            if (!a.isActive) return

            if (_.isEmpty(a.WB_ScheduleRow)) return

            if (a.cif.WB_SCHEDULE_ID == wbScheduleId) {
                found = true
                return false
            }
        })

        if (found) return

        const rel = <WB_JobParamSRRow>await dbContext.createRecordWithKey(esWB_Job_ParamSR, {})
        rel.cif.WB_JOB_PARAM_ID = jobParamRow.cif.WB_JOB_PARAM_ID
        rel.WB_JobParamRow = jobParamRow
        rel.cif.WB_SCHEDULE_ID = wbScheduleId
        rel.WB_ScheduleRow = scheduleRow
        dbContext.add(esWB_Job_ParamSR, rel)

        this.OnPropertyChanged.next({ JobParam: this, PropertyName: 'WB_ScheduleRow' })
    }

    async removeJobSchedule(scheduleRow: WB_ScheduleRow): Promise<void> {
        const jobParamRow = this._jobParamRow

        if (_.isEmpty(jobParamRow)) return

        if (_.isEmpty(scheduleRow)) return

        const wbScheduleId = scheduleRow?.cif?.WB_SCHEDULE_ID
        if (_.isUndefined(wbScheduleId)) return

        // Check if there is a duplicate
        let found = false
        _.forEach(jobParamRow.WB_JobParamSRRows, (a) => {
            if (!a.isActive) return

            if (_.isEmpty(a.WB_ScheduleRow)) return
            if (a.cif.WB_SCHEDULE_ID == scheduleRow.cif.WB_SCHEDULE_ID) {
                a.markDeleted()
                scheduleRow.markDeleted()
                found = true
                return false
            }
        })
        this.OnPropertyChanged.next({ JobParam: this, PropertyName: 'WB_ScheduleRow' })
    }

    async Delete(bSaveNow: boolean, options?: { bSkipValidations?: boolean }): Promise<void> {
        const dbContext = this._dbContext

        if (_.isEmpty(this._jobParamRow)) return
        this._bDeleted = false
        this._jobParamRow.markDeleted()
        _.forEach(this._jobParamRow.WB_JobParamSRRows, (a) => {
            a.markDeleted()
        })

        let tasks: Promise<any>[] = []
        if (bSaveNow) {
            tasks.push(dbContext.Save(esWB_Job_Param, this._jobParamRow))
            tasks.push(dbContext.Save(esWB_Job_ParamSR, this._jobParamRow.WB_JobParamSRRows))
        }

        await Promise.all(tasks)
        this._bDeleted = true
    }

    get bDeleted(): boolean {
        return this._bDeleted
    }
}
